import type { ImageBbox, InventoryItemResponse } from './types';

/**
 * Types for the segmentation-pipeline SSE endpoints.
 *
 * ## `POST /upload/scan/stream`
 *
 * One SSE connection runs both Vision (object detection) and SAM3
 * (pixel-level masks) with masks interleaved between items — masks
 * arrive per-item as soon as SAM3 returns, NOT batched after Vision
 * finishes. Rendering must honour this: never wait for `summary` or
 * `segmentation_complete` before drawing a mask.
 *
 * Event order (canonical):
 *   status → item (×N) → segmentation_started → mask (interleaved)
 *     → item_error? (×M) → mask_error? (×K) → summary
 *     → segmentation_complete → complete
 *
 * Every `mask.inventory_item_id` matches a prior `item.id`. `complete`
 * is the terminal event; consumers should close the stream after it.
 * An `error` event terminates mid-stream; items already delivered
 * remain persisted server-side.
 *
 * ## `GET /inventory/{id}/primary-photo/stream`
 *
 * One SSE connection per accepted item, opened in parallel after
 * POST /inventory/confirm/batch. Emits one-or-more `waiting` events
 * while the crop+upscale job runs, terminating in exactly one of:
 *   - primary_photo_ready   (happy path)
 *   - primary_photo_fallback (upstream failed; fall back to room photo)
 *   - primary_photo_timeout  (job didn't finish in 90s)
 *
 * See `docs/SEGMENTATION_PIPELINE_PRD.md` (backend repo) for the
 * authoritative spec; this file mirrors it 1:1.
 */

/* ─────────────────────────────────────────────────────────────── */
/* Scan stream payloads                                            */
/* ─────────────────────────────────────────────────────────────── */

export interface ScanStreamStatusPayload {
  message: string;
  file_record_id: number;
}

export interface ScanStreamSummaryPayload {
  ai_description: string;
  room_context: string;
}

export interface ScanStreamCompletePayload {
  success: boolean;
  message: string;
  file_record_id: number;
  content_type: 'image';
  /** Signed GCS URL for the full uploaded image (~24h TTL). Fallback
   *  source when a primary-photo job times out. */
  storage_url: string | null;
  ai_description: string;
  room_context: string;
  inventory_items: InventoryItemResponse[];
  items_count: number;
  vector_collections?: string[];
  /** New PRD name; kept `vector_collections` above as a fallback alias
   *  for older backend builds that haven't cut the rename yet. */
  collections_used?: string[];
  processing_time_ms: number;
}

export interface ScanStreamItemErrorPayload {
  error: string;
}

/**
 * Only present on partial-failure. `items_persisted` counts items
 * that were already delivered (and written to the DB) before the
 * stream died. Clients should keep those rows rendered.
 */
export interface ScanStreamErrorPayload {
  error: string;
  items_persisted?: number;
}

/* ─────────────────────────────────────────────────────────────── */
/* Segmentation payloads (new in feat/pipeline-v2)                 */
/* ─────────────────────────────────────────────────────────────── */

export interface ScanStreamSegmentationStartedPayload {
  file_record_id: number;
  /** Intentionally null — Vision may still be streaming when SAM3
   *  fans out; the final count lands on `segmentation_complete`. */
  items_count: number | null;
  /** NATURAL image dimensions in pixels. All bbox coordinates in
   *  subsequent `mask` events are absolute in this coord system.
   *  Cache these on the client — drag-to-annotate needs them to
   *  convert screen-percent back to absolute pixels. */
  image_width_px: number;
  image_height_px: number;
  model: 'sam3' | string;
}

export interface ScanStreamMaskPayload {
  inventory_item_id: number;
  /** The exact label text SAM3 ran the prompt ladder against. May
   *  differ from `prompt_text_used` if the original label was
   *  simplified on retry. */
  label_used?: string;
  bbox: ImageBbox;
  /** Signed GCS URL; PNG with alpha channel (opaque = object).
   *  NULL on the Gemini native-bbox path — the rectangle in `bbox`
   *  is the whole signal there; render the box only, no overlay. */
  mask_url: string | null;
  confidence: number;
  model:
    | 'sam3'
    | 'user_box'
    | 'user_box_fallback'
    | 'gemini_native_bbox'
    | string;
  /** Simplified prompt that actually matched (may be shorter than
   *  `label_used` after prompt-ladder retry). */
  prompt_text_used?: string;
  /** How many candidate masks SAM3 returned before the picker chose
   *  one. > 1 is surfaceable as "N candidates — we picked the best". */
  candidate_mask_count?: number;
}

/** Emitted when the primary scan pipeline (default: Gemini 3.1
 *  Flash-Lite) fails before yielding any item and the dispatcher
 *  falls back to SCAN_FALLBACK_PIPELINE (default: openai_default).
 *  Non-blocking — items + masks continue arriving on the fallback
 *  pipeline. UI should show a subtle "switched to backup model"
 *  affordance and keep waiting. */
export interface ScanStreamPipelineSwitchedPayload {
  from: string;
  to: string;
  /** Human-readable reason. Examples:
   *    'timeout_60s_no_first_event'
   *    'gemini_error_<ExceptionName>'
   *    'gemini_error_event:<msg>'
   *    'gemini_yielded_no_events' */
  reason: string;
}

export interface ScanStreamMaskErrorPayload {
  inventory_item_id: number;
  label_used?: string;
  error: string;
}

export interface ScanStreamSegmentationCompletePayload {
  file_record_id: number;
  masks_emitted: number;
  errors_emitted: number;
  items_count: number;
  processing_time_ms: number;
  estimated_cost_usd?: number;
  model: 'sam3' | string;
}

/* ─────────────────────────────────────────────────────────────── */
/* Scan stream event union                                         */
/* ─────────────────────────────────────────────────────────────── */

export type ScanStreamEvent =
  | { type: 'status'; data: ScanStreamStatusPayload }
  | { type: 'pipeline_switched'; data: ScanStreamPipelineSwitchedPayload }
  | { type: 'item'; data: InventoryItemResponse }
  | { type: 'item_error'; data: ScanStreamItemErrorPayload }
  | { type: 'segmentation_started'; data: ScanStreamSegmentationStartedPayload }
  | { type: 'mask'; data: ScanStreamMaskPayload }
  | { type: 'mask_error'; data: ScanStreamMaskErrorPayload }
  | { type: 'segmentation_complete'; data: ScanStreamSegmentationCompletePayload }
  | { type: 'summary'; data: ScanStreamSummaryPayload }
  | { type: 'complete'; data: ScanStreamCompletePayload }
  | { type: 'error'; data: ScanStreamErrorPayload };

/* ─────────────────────────────────────────────────────────────── */
/* Primary-photo stream payloads                                   */
/* ─────────────────────────────────────────────────────────────── */

export interface PrimaryPhotoWaitingPayload {
  inventory_item_id: number;
  poll_interval_seconds: number;
  timeout_seconds: number;
  elapsed_seconds: number;
}

export interface PrimaryPhotoReadyPayload {
  inventory_item_id: number;
  /** Signed GCS URL; RGBA PNG with transparent background. Render
   *  on a checkerboard backdrop in the Done view. */
  primary_photo_url: string;
  primary_photo_source: 'crop_upscale' | 'crop_only' | 'original';
  elapsed_seconds: number;
}

export interface PrimaryPhotoFallbackPayload {
  inventory_item_id: number;
  primary_photo_source: 'crop_upscale' | 'crop_only' | 'original';
  elapsed_seconds: number;
}

export interface PrimaryPhotoTimeoutPayload {
  inventory_item_id: number;
  elapsed_seconds: number;
  message: string;
}

export type PrimaryPhotoStreamEvent =
  | { type: 'waiting'; data: PrimaryPhotoWaitingPayload }
  | { type: 'primary_photo_ready'; data: PrimaryPhotoReadyPayload }
  | { type: 'primary_photo_fallback'; data: PrimaryPhotoFallbackPayload }
  | { type: 'primary_photo_timeout'; data: PrimaryPhotoTimeoutPayload };

/* ─────────────────────────────────────────────────────────────── */
/* REST types — correction endpoints                               */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Annotation interaction telemetry captured on every correction
 * endpoint (resegment / manual-add / delete). Writes one
 * `annotation_feedback` row server-side; harmless to omit.
 *
 * All integer fields MUST be non-negative integers — Pydantic
 * rejects fractional floats. Callers should `Math.round()` before
 * posting.
 */
export interface AnnotationInteraction {
  hesitation_ms?: number;
  draw_duration_ms?: number | null;
  device_kind?: 'mouse' | 'touch' | 'pen';
  cancel_count?: number;
}

export interface AnnotationPoint {
  x: number;
  y: number;
  /** 1 = foreground (include), 0 = background (exclude). */
  label: 0 | 1;
}

/* ── POST /inventory/{id}/resegment ───────────────────────────── */

export interface ResegmentRequest {
  /** Either `bbox` or `points` (or both) must be present. Coord
   *  system: absolute pixels in the NATURAL image. */
  bbox?: ImageBbox;
  /** Up to 32 foreground/background taps. Validated server-side. */
  points?: AnnotationPoint[];
  /** When true AND bbox is set AND points is empty: skip SAM3 and
   *  generate a rect mask from the bbox directly. Deterministic. */
  use_box_as_mask?: boolean;
  interaction?: AnnotationInteraction;
}

export interface ResegmentResponse {
  success: boolean;
  inventory_item_id: number;
  bbox: ImageBbox;
  mask_url: string;
  confidence: number;
  segmentation_model: 'sam3' | 'user_box' | 'user_box_fallback';
  feedback_id: number;
  iou_original_vs_new: number;
  area_ratio_new_over_original: number;
  centroid_shift_px: number;
}

/* ── POST /inventory/manual ───────────────────────────────────── */

export interface ManualAddInventoryItemRequest {
  file_record_id: number;
  item_name: string;
  bbox: ImageBbox;
  item_category?: string;
  estimated_value?: number;
  interaction?: AnnotationInteraction;
}

export interface ManualAddInventoryItemResponse {
  success: boolean;
  inventory_item: InventoryItemResponse;
}

/* ── POST /inventory/manual-analyze ───────────────────────────── */

/**
 * Tap-add AI flow. Replaces the legacy `/inventory/manual` +
 * `/inventory/{id}/re-analyze` two-call chain with a single
 * round-trip: the user taps (or drags) on the scan photo, the FE
 * manufactures / collects a bbox, posts here, and the BE returns a
 * fully AI-filled UserInventory row.
 *
 * Latency: 3–7s desktop, 1.5–4s mobile. NOT an SSE endpoint; one
 * JSON body when the work is done.
 *
 * Soft-failure: the BE returns 201 with `error` set to a non-null
 * string AND `item.item_name === "(unrecognized — tap to name)"`.
 * The row is real (bbox + mask persisted); just the AI-fill didn't
 * happen. UX: render with a warning chip + open rename input.
 */
export interface ManualAnalyzeRequest {
  file_record_id: number;
  bbox: ImageBbox;
  interaction?: AnnotationInteraction;
}

export interface ManualAnalyzeCitation {
  title?: string;
  url?: string;
  snippet?: string;
}

export interface ManualAnalyzeResponse {
  /** Same shape as the scan SSE `item` row; drop straight into the
   *  scan items list via `itemStreamed`. */
  item: InventoryItemResponse;
  /** Which row fields the analyzer wrote — useful for flash-highlight
   *  animations on AI-filled cells. */
  applied_fields: Record<string, unknown>;
  citations: ManualAnalyzeCitation[];
  crop_source: 'bbox_crop';
  cost_usd: number;
  /** Vision-call wall-time (subset of total round-trip). */
  latency_ms: number;
  web_grounding_enabled: boolean;
  pipeline_name: string | null;
  analyzer_provider: string | null;
  analyzer_model: string | null;
  /** Non-null in soft-failure mode. See module-level comment. */
  error: string | null;
}

/* ── DELETE /inventory/{id} (with feedback snapshot) ──────────── */

export interface DeleteInventoryItemV2Request {
  interaction?: AnnotationInteraction;
}

export interface DeleteInventoryItemV2Response {
  success: boolean;
  inventory_item_id: number;
  feedback_id: number;
}

/* ── POST /inventory/confirm/batch ────────────────────────────── */

/**
 * Per-item overrides (Sprint 2, BE-9.5).
 *
 * `group_ids` is the LIST of groups this item should be bound to. Empty
 * array = personal-only (the user explicitly opted out of all Crews for
 * this one item). Omitting the entry entirely (no extras row for an
 * item) means "use the picker's default selection".
 *
 * FE-214 — `serial_number` dropped per BE-202.
 */
export interface InventoryItemConfirmExtras {
  item_id: number;
  group_ids: number[];
}

export interface ConfirmBatchRequest {
  file_record_id: number;
  accepted_item_ids: number[];
  rejected_item_ids: number[];
  /** Sprint 2 — captured by FE-9.2, rounded to 4dp client-side. */
  scan_lat?: number;
  /** Sprint 2 — captured by FE-9.2, rounded to 4dp client-side. */
  scan_lng?: number;
  /** Sprint 2 — per-item group binding overrides. */
  extras?: InventoryItemConfirmExtras[];
}

export interface ConfirmBatchResponse {
  success: boolean;
  file_record_id: number;
  accepted_count: number;
  rejected_count: number;
  accepted_item_ids: number[];
  rejected_item_ids: number[];
  primary_photo_jobs: Array<{ inventory_item_id: number; status: 'queued' | string }>;
  vector_delete_failures: unknown[];
  /** Sprint 2 — how many `item_group` rows were inserted by this call. */
  item_group_bindings_added?: number;
  // FE-214 — `serial_numbers_set` removed (column dropped on BE-202).
  /** Sprint 2 — true if `scan_lat`/`scan_lng` were persisted on items. */
  scan_location_set?: boolean;
}

/* ── GET /admin/annotation-feedback ───────────────────────────── */

export type AnnotationFeedbackEventType =
  | 'resegment'
  | 'manual_add'
  | 'reject'
  | 'delete';

export interface AnnotationFeedbackRow {
  id: number;
  user_id: number;
  inventory_item_id: number;
  file_record_id: number;
  event_type: AnnotationFeedbackEventType;
  original_bbox: ImageBbox | null;
  original_mask_url: string | null;
  original_confidence: number | null;
  original_candidate_mask_count: number | null;
  segmentation_model: string | null;
  new_bbox: ImageBbox | null;
  new_mask_url: string | null;
  new_confidence: number | null;
  iteration: number;
  hesitation_ms: number | null;
  draw_duration_ms: number | null;
  device_kind: string | null;
  cancel_count: number | null;
  item_name_snapshot: string | null;
  item_category_snapshot: string | null;
  item_description_snapshot: string | null;
  room_context_snapshot: string | null;
  visual_area_pct_snapshot: number | null;
  prominence_score_snapshot: number | null;
  iou_original_vs_new: number | null;
  area_ratio_new_over_original: number | null;
  centroid_shift_px: number | null;
  created_at: string;
}

export interface AdminAnnotationFeedbackParams {
  limit?: number;
  cursor?: string;
}

/* ─────────────────────────────────────────────────────────────── */
/* Errors                                                          */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Thrown when the initial POST returns a 4xx/5xx (i.e. before the
 * SSE stream actually begins). Consumers branch on `instanceof` to
 * decide between "show the structured HTTP error" vs "generic
 * network/stream failure".
 */
export class ScanHttpError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(`HTTP ${status}: ${detail}`);
    this.name = 'ScanHttpError';
  }
}
